import { Component, EventEmitter, Output, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';

@Component({
    selector: 'app-delete-simulation-filter',
    templateUrl: './delete-simulation-filter.component.html',
    styleUrls: ['./delete-simulation-filter.component.css']
})
// --- Component Description ---
// Component for filtering simulations by hierarchical entities (user, customer, region, project, simulation).
// Provides a dynamic filter form that updates available options and filtered lists
// based on the current selection at each level. Emits filter changes to parent components.
// Hierarchy: User → Customer → Region → Project → Simulation
export class DeleteSimulationFilterComponent implements OnInit, OnChanges {
    private hasAutoSelectedUser = false;
    private selectedUser: any;

    @Input() customers: any[] = [];
    @Input() regions: any[] = [];
    @Input() projects: any[] = [];
    @Input() simulations: any[] = [];

    filteredCustomers: any[] = [];
    filteredRegions: any[] = [];
    filteredProjects: any[] = [];
    filteredSimulations: any[] = [];

    @Output() filterChange = new EventEmitter<any>();

    filterForm: UntypedFormGroup;

    ngOnInit() {
        this.filterForm = new UntypedFormGroup({
            customer: new UntypedFormControl(null),
            region: new UntypedFormControl(null),
            project: new UntypedFormControl(null),
            simulation: new UntypedFormControl(null)
        });

        this.filteredCustomers = [...this.customers];
        this.filteredRegions = [...this.regions];
        this.filteredProjects = [...this.projects];
        this.filteredSimulations = [...this.simulations];

        this.filterForm.get('customer').valueChanges.subscribe(customer => {
            this.filterByCustomer(customer);
        });

        this.filterForm.get('region').valueChanges.subscribe(region => {
            this.filterByRegion(region);
        });

        this.filterForm.get('project').valueChanges.subscribe(project => {
            this.filterByProject(project);
        });

        this.filterForm.get('simulation').valueChanges.subscribe(simulation => {
            this.filterBySimulation(simulation);
        });

        this.filterForm.valueChanges.subscribe(val => {
            this.filterChange.emit(val);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this.filteredCustomers = [...this.customers];
        this.filteredRegions = [...this.regions];
        this.filteredProjects = [...this.projects];
        this.filteredSimulations = [...this.simulations];
        this.filterForm?.updateValueAndValidity({ emitEvent: true });
    }

    filterByCustomer(customer: any) {
        if (!customer) {
            this.resetFilters(['region', 'project', 'simulation']);
            return;
        }
        this.filteredRegions = this.regions.filter(r => r.parent_id === customer.id);
        const regionIds = this.filteredRegions.map(r => r.id);
        this.filteredProjects = this.projects.filter(p => regionIds.includes(p.parent_id));
        const projectIds = this.filteredProjects.map(p => p.id);
        this.filteredSimulations = this.simulations.filter(s => projectIds.includes(s.parent_id));
        this.clearIfNotIncluded('region', regionIds);
        this.clearIfNotIncluded('project', projectIds);
        this.clearIfNotIncluded('simulation', this.filteredSimulations.map(s => s.id));
    }

    filterByRegion(region: any) {
        if (!region) {
            this.resetFilters(['project', 'simulation']);
            this.filterByCustomer(this.filterForm.get('customer').value);
            return;
        }
        const regionId = region.id;
        const customer = this.customers.find(c => c.id === region.parent_id);
        this.filteredCustomers = customer ? [customer] : [];
        this.filteredProjects = this.projects.filter(p => p.parent_id === regionId);
        const projectIds = this.filteredProjects.map(p => p.id);
        this.filteredSimulations = this.simulations.filter(s => projectIds.includes(s.parent_id));
        this.setFilterValue('customer', customer);
        this.clearIfNotIncluded('project', projectIds);
        this.clearIfNotIncluded('simulation', this.filteredSimulations.map(s => s.id));
    }

    filterByProject(project: any) {
        if (!project) {
            this.resetFilters(['simulation']);
            this.filterByRegion(this.filterForm.get('region').value);
            return;
        }
        this.filteredSimulations = this.simulations.filter(s => s.parent_id === project.id);
        const region = this.regions.find(r => r.id === project.parent_id);
        this.filteredRegions = region ? [region] : [];
        const customer = region ? this.customers.find(c => c.id === region.parent_id) : null;
        this.filteredCustomers = customer ? [customer] : [];
        this.setFilterValue('region', region);
        this.setFilterValue('customer', customer);
        this.clearIfNotIncluded('simulation', this.filteredSimulations.map(s => s.id));
    }

    filterBySimulation(simulation: any) {
        if (!simulation) {
            this.filterByProject(this.filterForm.get('project').value);
            return;
        }
        const project = this.projects.find(p => p.id === simulation.parent_id);
        this.filteredProjects = project ? [project] : [];
        const region = project ? this.regions.find(r => r.id === project.parent_id) : null;
        this.filteredRegions = region ? [region] : [];
        const customer = region ? this.customers.find(c => c.id === region.parent_id) : null;
        this.filteredCustomers = customer ? [customer] : [];
        this.setFilterValue('project', project);
        this.setFilterValue('region', region);
        this.setFilterValue('customer', customer);
    }

    // --- Public Methods ---
    // Resets the given filter fields to null without emitting events.
    resetFilters(fields: string[], isResetBtnEvent: boolean = false) {
        if (isResetBtnEvent) {
            fields.forEach(f => {
                if (f === 'user' && isResetBtnEvent && this.selectedUser) {
                    this.filterForm.get('user').setValue(this.selectedUser, { emitEvent: false });
                } else {
                    this.filterForm.get(f).setValue(null, { emitEvent: false });
                }
            });

            this.filterChange.emit(this.filterForm.value);
        } else {
            fields.forEach(f => this.filterForm.get(f).setValue(null, { emitEvent: false }));
        }
    }

    setAllFiltered() {
        this.filteredCustomers = [...this.customers];
        this.filteredRegions = [...this.regions];
        this.filteredProjects = [...this.projects];
        this.filteredSimulations = [...this.simulations];
    }

    private setFilterValue(field: string, value: any) {
        this.filterForm.get(field).setValue(value ? value : null, { emitEvent: false });
    }

    private clearIfNotIncluded(field: string, validIds: any[]) {
        const val = this.filterForm.get(field).value;
        if (val && !validIds.includes(val.id)) {
            this.filterForm.get(field).setValue(null);
        }
    }
}